import React from 'react';
import { HealthCalendarTile } from './HealthCalendarTile';
import { ThisWeekTile } from './ThisWeekTile';
import { WeightGaugeV2 } from './WeightGaugeV2';
import { StreaksTile } from './StreaksTile';
import { TimeToGoalTile } from './TimeToGoalTile';
import { useWeightProgress } from '../../hooks/useWeightProgress';
import { useAuth } from '../../contexts/AuthContext';

/**
 * CLIENT PROGRESS PAGE
 *
 * The "Health Center" for clients - answers "How is my journey going?"
 *
 * Layout:
 * - Top row: Calendar (1/2) + side stack (1/2) with ThisWeek + Streaks
 * - Bottom row (Ph1+): Weight Progress + Time to Goal (2 columns)
 *
 * Ph0 clients only see Calendar + This Week (progressive disclosure).
 * All weight-related tiles share data via useWeightProgress hook.
 */

const ClientProgress: React.FC = () => {
  const { user } = useAuth();
  const clientPhase = user?.client_phase || 0;
  const { data: weightData, loading: weightLoading, error: weightError } = useWeightProgress();

  return (
    <div className="client-progress">
      {/* Page Header */}
      <div className="progress-header">
        <h1>Progress</h1>
        <p className="header-subtitle">Track your health journey</p>
      </div>

      {/* Top Row: Calendar + Side Stack */}
      <div className="progress-section top-row">
        {/* Health Calendar - 1/2 width */}
        <div className="calendar-container">
          <HealthCalendarTile />
        </div>

        {/* Side Stack - 1/2 width */}
        <div className="side-stack">
          <ThisWeekTile />
          {clientPhase >= 1 && (
            <StreaksTile data={weightData} loading={weightLoading} />
          )}
        </div>
      </div>

      {/* Bottom Row - Ph1+ only */}
      {clientPhase >= 1 && (
        <div className="progress-section bottom-row">
          <WeightGaugeV2 data={weightData} loading={weightLoading} error={weightError} />
          <TimeToGoalTile data={weightData} loading={weightLoading} />
        </div>
      )}

      <style>{`
        .client-progress {
          display: flex;
          flex-direction: column;
          gap: 16px;
          width: 100%;
        }

        .progress-header {
          margin-bottom: 4px;
        }

        .progress-header h1 {
          font-size: 24px;
          font-weight: 600;
          color: var(--text-primary);
          margin: 0 0 4px 0;
        }

        .header-subtitle {
          font-size: 14px;
          color: var(--text-secondary);
          margin: 0;
        }

        .progress-section {
          display: flex;
          gap: 16px;
        }

        /* Top Row: Calendar (2 cols) + Side Area (2 cols) - 4 column grid */
        .top-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          align-items: stretch;
        }

        .calendar-container {
          min-height: 350px;
        }

        .side-stack {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
          align-content: start;
        }

        .side-stack .progress-card {
          min-height: 160px;
        }

        /* Bottom Row - 4 column grid, tiles take first 2 slots */
        .bottom-row {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
        }

        /* Card Base Styles */
        .progress-card {
          background: var(--bg-secondary);
          border: 1px solid var(--border-secondary);
          border-radius: var(--radius-large);
          padding: 16px;
          display: flex;
          flex-direction: column;
        }

        .card-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
          padding: 0;
          border-bottom: none;
        }

        .card-header h3 {
          font-size: 14px;
          font-weight: 600;
          color: var(--text-primary);
          margin: 0;
        }

        .card-icon {
          color: var(--accent-blue);
        }

        .card-icon.streak-icon {
          color: #f59e0b;
        }

        .card-icon.goal-icon {
          color: var(--accent-purple);
        }

        .card-content {
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        /* Responsive */
        @media (max-width: 1200px) {
          .bottom-row {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 900px) {
          .top-row {
            grid-template-columns: 1fr;
          }

          .side-stack {
            grid-template-columns: repeat(2, 1fr);
          }

          .bottom-row {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 600px) {
          .side-stack {
            grid-template-columns: 1fr;
          }

          .bottom-row {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default ClientProgress;
